import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { formatDistance, differenceInMinutes, parseISO } from 'date-fns';
import { sessionsService } from '../../services';
import { Session } from '../../types';
import { SessionStatusBadge } from './SessionStatusBadge';
import { SessionFilters, type SessionFilters as SessionFiltersType } from './SessionFilters';
import { Pagination } from './Pagination';
import { formatDuration, formatDateTimeInBrussels, formatTimeInBrussels } from '../../utils/format';

export const SessionsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [endingSessionId, setEndingSessionId] = useState<string | null>(null);
  const [activeSessionsExpanded, setActiveSessionsExpanded] = useState(true);
  const [endedSessionsExpanded, setEndedSessionsExpanded] = useState(false);

  // Historical sessions state
  const ITEMS_PER_PAGE = 25;
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<SessionFiltersType>({
    dateRange: 'today',
    startDate: new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
    endDate: new Date().toISOString(),
  });

  // Sort state for historical sessions
  type SortColumn = 'assignment' | 'startedAt' | 'endedAt' | 'duration' | 'driverId' | 'busId' | 'status' | 'positionCount';
  type SortDirection = 'asc' | 'desc';

  const [sortConfig, setSortConfig] = useState<{
    column: SortColumn;
    direction: SortDirection;
  }>({
    column: 'startedAt',
    direction: 'desc', // Most recent first
  });

  // Handle sort
  const handleSort = (column: SortColumn) => {
    setSortConfig((prev) => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1); // Reset to page 1 when sorting
  };

  // Fetch active sessions
  const {
    data: activeSessions,
    isLoading: activeLoading,
    error: activeError,
  } = useQuery({
    queryKey: ['sessions', 'active'],
    queryFn: () => sessionsService.getActiveSessions(),
    refetchInterval: 10000, // Auto-refresh every 10 seconds (was 5s)
  });

  // End session mutation
  const endSessionMutation = useMutation({
    mutationFn: (sessionId: string) => sessionsService.endSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
      setEndingSessionId(null);
    },
    onError: (error) => {
      console.error('Failed to end session:', error);
      alert('Failed to end session. Please try again.');
      setEndingSessionId(null);
    },
  });

  // Handle end session
  const handleEndSession = (sessionId: string) => {
    if (!window.confirm('Are you sure you want to end this session?')) {
      return;
    }

    setEndingSessionId(sessionId);
    endSessionMutation.mutate(sessionId);
  };

  // Handle view on map
  const handleViewOnMap = (sessionId: string) => {
    navigate(`/map?sessionId=${sessionId}`);
  };

  // Handle view route
  const handleViewRoute = (sessionId: string) => {
    navigate(`/positions/${sessionId}`);
  };

  // Check if position is stale (> 5 minutes)
  const isStale = (lastPositionAt: string | null): boolean => {
    if (!lastPositionAt) return true;
    const diffMinutes = differenceInMinutes(new Date(), parseISO(lastPositionAt));
    return diffMinutes > 5;
  };

  // Fetch ended sessions
  const {
    data: endedSessionsData,
    isLoading: endedLoading,
  } = useQuery({
    queryKey: ['sessions', 'ended', filters, currentPage],
    queryFn: () => sessionsService.getEndedSessions(),
    enabled: endedSessionsExpanded,
  });

  // Filter ended sessions locally (since backend doesn't support filters yet)
  const filteredEndedSessions = useMemo(() => {
    if (!endedSessionsData) return [];

    let filtered = endedSessionsData;

    // Filter by date range
    if (filters.startDate && filters.endDate) {
      const startDate = parseISO(filters.startDate);
      const endDate = parseISO(filters.endDate);
      filtered = filtered.filter((session) => {
        const sessionDate = parseISO(session.startedAt || session.createdAt || '');
        return sessionDate >= startDate && sessionDate <= endDate;
      });
    }

    // Filter by driver
    if (filters.driverId) {
      filtered = filtered.filter((session) =>
        session.driverId.toLowerCase().includes(filters.driverId!.toLowerCase())
      );
    }

    // Filter by bus
    if (filters.busId) {
      filtered = filtered.filter((session) =>
        session.busId.toLowerCase().includes(filters.busId!.toLowerCase())
      );
    }

    // Sort
    return filtered.sort((a, b) => {
      let compareValue = 0;

      switch (sortConfig.column) {
        case 'startedAt':
          const aStart = a.startedAt ? new Date(a.startedAt).getTime() : 0;
          const bStart = b.startedAt ? new Date(b.startedAt).getTime() : 0;
          compareValue = aStart - bStart;
          break;
        case 'endedAt':
          const aEnd = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          const bEnd = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
          compareValue = aEnd - bEnd;
          break;
        case 'duration':
          const aDuration = a.startedAt && a.updatedAt
            ? new Date(a.updatedAt).getTime() - new Date(a.startedAt).getTime()
            : 0;
          const bDuration = b.startedAt && b.updatedAt
            ? new Date(b.updatedAt).getTime() - new Date(b.startedAt).getTime()
            : 0;
          compareValue = aDuration - bDuration;
          break;
        case 'driverId':
          compareValue = a.driverId.localeCompare(b.driverId);
          break;
        case 'busId':
          compareValue = (a.busId || '').localeCompare(b.busId || '');
          break;
        case 'status':
          compareValue = a.status.localeCompare(b.status);
          break;
        case 'assignment':
          const aAssignment = `${a.transportId || ''}-${a.journeyId || ''}`;
          const bAssignment = `${b.transportId || ''}-${b.journeyId || ''}`;
          compareValue = aAssignment.localeCompare(bAssignment);
          break;
        case 'positionCount':
          compareValue = (a.positionCount || 0) - (b.positionCount || 0);
          break;
      }

      return sortConfig.direction === 'asc' ? compareValue : -compareValue;
    });
  }, [endedSessionsData, filters, sortConfig]);

  // Paginate results
  const paginatedSessions = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return filteredEndedSessions.slice(start, end);
  }, [filteredEndedSessions, currentPage]);

  const totalPages = Math.ceil(filteredEndedSessions.length / ITEMS_PER_PAGE);

  // Handle filter changes
  const handleFiltersChange = (newFilters: SessionFiltersType) => {
    setFilters(newFilters);
  };

  const handleFiltersApply = () => {
    setCurrentPage(1);
  };

  const handleFiltersClear = () => {
    setCurrentPage(1);
    setFilters({
      dateRange: 'today',
      startDate: new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
      endDate: new Date().toISOString(),
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Sortable header component
  const SortableHeader = ({
    column,
    children,
  }: {
    column: SortColumn;
    children: React.ReactNode;
  }) => {
    const isActive = sortConfig.column === column;
    return (
      <th
        className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-100 select-none transition"
        onClick={() => handleSort(column)}
      >
        <div className="flex items-center gap-1">
          {children}
          {isActive ? (
            sortConfig.direction === 'asc' ? (
              <span className="text-blue-600 font-bold">↑</span>
            ) : (
              <span className="text-blue-600 font-bold">↓</span>
            )
          ) : (
            <span className="text-gray-300">↕</span>
          )}
        </div>
      </th>
    );
  };

  if (activeLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (activeError) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-800 font-medium">Error loading sessions</p>
        <p className="text-red-600 text-sm">{String(activeError)}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Title */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Sessions</h1>
        <p className="text-gray-600 mt-1">Manage and monitor all sessions</p>
      </div>

      {/* Active Sessions Section */}
      <section>
        {/* Section Header - Collapsible */}
        <button
          onClick={() => setActiveSessionsExpanded(!activeSessionsExpanded)}
          className="w-full flex items-center justify-between p-4 bg-white rounded-lg shadow mb-4 hover:bg-gray-50 transition"
        >
          <div className="flex items-center gap-2">
            <span>{activeSessionsExpanded ? '▼' : '▶'}</span>
            <h2 className="text-xl font-semibold text-gray-900">
              Active Sessions ({activeSessions?.length || 0})
            </h2>
          </div>
          <div className="text-sm text-gray-500">
            {activeSessions && activeSessions.length > 0 && (
              <>Last updated: {new Date().toLocaleTimeString()}</>
            )}
          </div>
        </button>

        {/* Active Sessions Table */}
        {activeSessionsExpanded && (
          <>
            {activeSessions && activeSessions.length > 0 ? (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                          Session ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                          Driver
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                          Bus
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                          Assignment
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                          Started
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                          Last Position
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                          Positions
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {activeSessions.map((session: Session) => {
                        const lastPositionText = session.lastPositionAt
                          ? formatDistance(new Date(session.lastPositionAt), new Date(), {
                              addSuffix: true,
                            })
                          : 'No positions yet';

                        const stale = isStale(session.lastPositionAt || null);
                        const isEnding = endingSessionId === session.sessionId;

                        return (
                          <tr key={session.sessionId} className="hover:bg-gray-50">
                            <td className="px-6 py-4 text-sm font-mono text-gray-600">
                              <div className="flex items-center gap-2">
                                <span>{session.sessionId.substring(0, 8)}...</span>
                                <SessionStatusBadge status={session.status} size="sm" />
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900">{session.driverId}</td>
                            <td className="px-6 py-4 text-sm text-gray-900">{session.busId}</td>
                            <td className="px-6 py-4 text-sm text-gray-900">
                              <div className="font-medium">
                                {session.transportId ? `T${session.transportId}` : 'N/A'}
                              </div>
                              <div className="text-xs text-gray-500">
                                {session.journeyId ? `J${session.journeyId}` : 'N/A'}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600">
                              {session.startedAt
                                ? formatTimeInBrussels(session.startedAt)
                                : '—'}
                            </td>
                            <td
                              className={`px-6 py-4 text-sm ${
                                stale ? 'text-red-600 font-medium' : 'text-gray-600'
                              }`}
                            >
                              {lastPositionText} {stale && '⚠️'}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600">{session.positionCount || 0}</td>
                            <td className="px-6 py-4 text-sm space-y-2">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEndSession(session.sessionId)}
                                  disabled={isEnding}
                                  className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400 text-xs font-medium transition"
                                  title="End this session"
                                >
                                  {isEnding ? 'Ending...' : 'End'}
                                </button>
                                <button
                                  onClick={() => handleViewOnMap(session.sessionId)}
                                  className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs font-medium transition"
                                  title="View on map"
                                >
                                  🗺️ Map
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6 text-center">
                <p className="text-gray-500">No active sessions at the moment</p>
              </div>
            )}
          </>
        )}
      </section>

      {/* Historical Sessions Section */}
      <section>
        {/* Section Header - Collapsible */}
        <button
          onClick={() => setEndedSessionsExpanded(!endedSessionsExpanded)}
          className="w-full flex items-center justify-between p-4 bg-white rounded-lg shadow mb-4 hover:bg-gray-50 transition"
        >
          <div className="flex items-center gap-2">
            <span>{endedSessionsExpanded ? '▼' : '▶'}</span>
            <h2 className="text-xl font-semibold text-gray-900">
              Historical Sessions ({filteredEndedSessions.length})
            </h2>
          </div>
        </button>

        {/* Filters and Historical Sessions Table */}
        {endedSessionsExpanded && (
          <>
            {/* Filters */}
            <SessionFilters
              initialFilters={filters}
              onChange={handleFiltersChange}
              onApply={handleFiltersApply}
              onClear={handleFiltersClear}
              isLoading={endedLoading}
            />

            {/* Sessions Table */}
            {filteredEndedSessions.length > 0 ? (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <SortableHeader column="assignment">Assignment</SortableHeader>
                        <SortableHeader column="startedAt">Started</SortableHeader>
                        <SortableHeader column="endedAt">Ended</SortableHeader>
                        <SortableHeader column="duration">Duration</SortableHeader>
                        <SortableHeader column="driverId">Driver</SortableHeader>
                        <SortableHeader column="busId">Bus</SortableHeader>
                        <SortableHeader column="status">Status</SortableHeader>
                        <SortableHeader column="positionCount">Positions</SortableHeader>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {paginatedSessions.map((session: Session) => (
                        <tr key={session.sessionId} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            <div>
                              {session.transportId && session.journeyId
                                ? `T${session.transportId} → J${session.journeyId}`
                                : 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {session.startedAt
                              ? formatDateTimeInBrussels(session.startedAt)
                              : '—'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {session.updatedAt
                              ? formatDateTimeInBrussels(session.updatedAt)
                              : '—'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {session.startedAt && session.updatedAt
                              ? formatDuration(session.startedAt, session.updatedAt)
                              : '—'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">{session.driverId}</td>
                          <td className="px-6 py-4 text-sm text-gray-900">{session.busId}</td>
                          <td className="px-6 py-4 text-sm">
                            <SessionStatusBadge status={session.status} size="sm" />
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {session.positionCount || 0}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <button
                              onClick={() => handleViewRoute(session.sessionId)}
                              className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs font-medium transition"
                              title="View route on map"
                            >
                              📍 Itinéraire
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="bg-white border-t border-gray-200 px-6 py-4">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={filteredEndedSessions.length}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={handlePageChange}
                  />
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6 text-center">
                <p className="text-gray-500">
                  {endedLoading ? 'Chargement...' : 'Aucune session trouvée pour ces critères'}
                </p>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};
